import * as React from 'react';

import { LucideAsterisk, LucideLoader } from 'lucide-react';

import { cn } from '../../lib/stylingUtils';
import { ThemeColor } from '../../types';

const MAX_LENGTH_OF_RELEVANT_DESCRIPTION = 6;
const MAX_SINGLE_LINE_DESCRIPTION_LENGTH =
  (MAX_LENGTH_OF_RELEVANT_DESCRIPTION * 2) / 3;

type MaterialSymbolProps = {
  isLoading?: boolean;
  className?: string;
  children?: React.ReactNode;
  description?: string;
  isDeduction?: boolean;
  color?: ThemeColor;
  symbolIcon?: React.ReactNode;
  style?: React.CSSProperties;
};

const materialSymbolColorClasses: Record<string, string> = {
  purple: 'bg-purple/20 text-purple',
  rust: 'bg-rust/20 text-rust',
  teal: 'bg-teal/10 text-teal',
  slate: 'bg-slate/20 text-slate',
  olive: 'bg-olive/10 text-olive',
  primary: 'bg-primary/20 text-primary',
  transparent: 'bg-transparent text-neutral',
  failure: 'bg-failure/10 text-failure',
  neutral: 'bg-neutral/10 text-neutral',
};

export const MaterialSymbol = ({
  description,
  isLoading,
  className,
  children,
  color = 'neutral',
  symbolIcon,
  style,
}: MaterialSymbolProps) => {
  const relevantDescription = React.useMemo(() => {
    const currentDescription = description?.replace(/_/g, ' ') ?? '';
    return currentDescription.length > MAX_LENGTH_OF_RELEVANT_DESCRIPTION
      ? undefined
      : currentDescription;
  }, [description]);

  const hasLongDescription =
    relevantDescription &&
    relevantDescription.length > MAX_SINGLE_LINE_DESCRIPTION_LENGTH;

  return (
    <div
      className={cn(
        '@MaterialSymbol',
        'flex h-full min-h-[30px] min-w-[38px] flex-col items-center justify-center rounded px-2 text-[11px] leading-none font-medium',
        materialSymbolColorClasses[color],
        className,
      )}
      style={style}
    >
      {(() => {
        if (isLoading)
          return <LucideLoader size={18} className="animate-spin" />;
        if (children) return children;
        if (symbolIcon)
          return (
            <div className="flex flex-col items-center gap-1">
              {symbolIcon}
              {relevantDescription && !hasLongDescription && (
                <div>{relevantDescription}</div>
              )}
            </div>
          );
        if (hasLongDescription)
          return (
            <>
              <div>
                {relevantDescription.substring(
                  0,
                  relevantDescription.length / 2,
                )}
              </div>
              <div>
                {relevantDescription.substring(relevantDescription.length / 2)}
              </div>
            </>
          );
        if (relevantDescription) return relevantDescription;
        return <LucideAsterisk size={18} />;
      })()}
    </div>
  );
};
